import { supabase } from '../lib/supabase';

// Function to replace all image URLs in HTML content with Supabase URLs
export const replaceImagesInContent = async (content: string): Promise<string> => {
  if (!content) return content;
  
  // Regular expression to find image tags
  const imgRegex = /<img[^>]+src="([^"]+)"[^>]*>/g;
  
  let match;
  let processedContent = content;
  const replacements: { original: string, replacement: string }[] = [];
  
  // Find all image tags and collect replacements
  while ((match = imgRegex.exec(content)) !== null) {
    const fullTag = match[0];
    const srcUrl = match[1];
    
    // Skip if already a Supabase URL
    if (srcUrl.includes(import.meta.env.VITE_SUPABASE_URL)) continue;
    
    // Skip data URLs
    if (srcUrl.startsWith('data:')) continue;
    
    try {
      // Generate a unique path for the image
      const urlParts = srcUrl.split('/');
      let filename = urlParts[urlParts.length - 1];
      
      // Clean up filename and ensure it has an extension
      if (!filename.includes('.')) {
        if (srcUrl.includes('.jpg') || srcUrl.includes('.jpeg')) {
          filename += '.jpg';
        } else if (srcUrl.includes('.png')) {
          filename += '.png';
        } else if (srcUrl.includes('.svg')) {
          filename += '.svg';
        } else if (srcUrl.includes('.gif')) {
          filename += '.gif';
        } else {
          filename += '.jpg';
        }
      }
      
      // Add timestamp to ensure uniqueness
      const timestamp = Date.now();
      const parts = filename.split('.');
      const extension = parts.pop() || 'jpg';
      const name = parts.join('.');
      const path = `content/${name}-${timestamp}.${extension}`;
      
      // Download the image
      const imageResponse = await fetch(srcUrl);
      if (!imageResponse.ok) continue;
      
      const imageBlob = await imageResponse.blob();
      
      // Upload to Supabase Storage
      const { data, error } = await supabase.storage
        .from('vmon')
        .upload(path, imageBlob, {
          cacheControl: '3600',
          upsert: true,
        });

      if (error) throw error;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('vmon')
        .getPublicUrl(path);
      
      // Create replacement
      const newTag = fullTag.replace(srcUrl, publicUrl);
      replacements.push({ original: fullTag, replacement: newTag });
      
    } catch (error) {
      console.error('Error processing image:', srcUrl, error);
    }
  }
  
  // Apply all replacements
  for (const { original, replacement } of replacements) {
    processedContent = processedContent.replace(original, replacement);
  }
  
  return processedContent;
};

// Function to replace image URL in a field
export const replaceImageUrl = async (url: string): Promise<string> => {
  if (!url) return url;
  
  // Skip if already a Supabase URL
  if (url.includes(import.meta.env.VITE_SUPABASE_URL)) return url;
  
  // Skip data URLs
  if (url.startsWith('data:')) return url;
  
  try {
    // Generate a unique path for the image
    const urlParts = url.split('/');
    let filename = urlParts[urlParts.length - 1];
    
    // Clean up filename and ensure it has an extension
    if (!filename.includes('.')) {
      if (url.includes('.jpg') || url.includes('.jpeg')) {
        filename += '.jpg';
      } else if (url.includes('.png')) {
        filename += '.png';
      } else if (url.includes('.svg')) {
        filename += '.svg';
      } else if (url.includes('.gif')) {
        filename += '.gif';
      } else {
        filename += '.jpg';
      }
    }
    
    // Add timestamp to ensure uniqueness
    const timestamp = Date.now();
    const parts = filename.split('.');
    const extension = parts.pop() || 'jpg';
    const name = parts.join('.');
    const path = `featured/${name}-${timestamp}.${extension}`;
    
    // Download the image
    const imageResponse = await fetch(url);
    if (!imageResponse.ok) return url;
    
    const imageBlob = await imageResponse.blob();
    
    // Upload to Supabase Storage
    const { data, error } = await supabase.storage
      .from('vmon')
      .upload(path, imageBlob, {
        cacheControl: '3600',
        upsert: true,
      });

    if (error) throw error;

    // Get public URL
    const { data: { publicUrl } } = supabase.storage
      .from('vmon')
      .getPublicUrl(path);
    
    return publicUrl;
  } catch (error) {
    console.error('Error replacing image URL:', url, error);
    return url;
  }
};